import { useQuery } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import { Clock, Download, Loader2, X } from "lucide-react";
import { Heading, Text } from "@/components/Typography";
import { api } from "@/services/api";
import { cn } from "@/utils/cn";
import { PdfCarousel } from "./PdfCarousel";

/**
 * The LinkedIn spotlight carousel, as the expert will post it.
 *
 * The team prepares the carousel as a PDF and an admin uploads it per expert
 * from the admin panel (Users → LinkedIn Carousel). Here it is previewed page
 * by page, the way LinkedIn shows a document post, and can be downloaded to
 * attach to the expert's own post.
 *
 * Until a PDF exists the modal says so and offers no Approve: there is nothing
 * to approve yet.
 */

const DOWNLOAD_NAME = "LinkedIn-carousel.pdf";

/** Saves the bytes already fetched for the preview — no second request. */
const downloadPdf = (data: ArrayBuffer) => {
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = DOWNLOAD_NAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Deferred: revoking in the same tick can cancel the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const CarouselPreviewModal = ({
  open,
  onOpenChange,
  onApprove,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: () => void;
}) => {
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ["getting-started-carousel"],
    queryFn: () => api.gettingStarted.getCarousel(),
    enabled: open,
    // Always rechecked on open: the admin may have uploaded it since.
    staleTime: 0,
  });
  const available = !!status?.available;

  const {
    data: file,
    isLoading: fileLoading,
    isError: fileError,
  } = useQuery({
    // Keyed on the upload time, so a replaced PDF is fetched afresh.
    queryKey: ["getting-started-carousel-file", status?.uploadedAt ?? null],
    queryFn: () => api.gettingStarted.getCarouselFile(),
    enabled: open && available,
    staleTime: Infinity,
  });

  const loading = statusLoading || (available && fileLoading);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex max-h-[94vh] w-[calc(100vw-32px)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col",
            "rounded-2xl border border-theme-border bg-theme-bg-surface p-6 shadow-xl focus:outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
        >
          <Dialog.Title asChild>
            <Heading
              as="h2"
              className="pr-8 text-[17px] font-bold tracking-[-0.2px] text-theme-text-heading"
            >
              Your spotlight carousel
            </Heading>
          </Dialog.Title>
          <Dialog.Description asChild>
            <Text as="p" className="mt-1 text-[12.5px] leading-[1.5] text-theme-text-secondary">
              {available
                ? "A preview of how your PDF will look as a LinkedIn document post. Download it to attach to your post."
                : "Our team prepares your carousel as a PDF from your S.T.A.R.s."}
            </Text>
          </Dialog.Description>

          <Dialog.Close asChild>
            <button
              aria-label="Close"
              className="absolute right-4 top-4 text-theme-text-muted transition-colors hover:text-theme-text-heading"
            >
              <X size={18} />
            </button>
          </Dialog.Close>

          <div className="mt-5 min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex h-[300px] items-center justify-center rounded-xl border border-theme-border-subtle bg-theme-bg-subtle text-theme-text-muted">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : !available ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-theme-border-strong bg-theme-bg-subtle px-6 py-12 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-theme-accent-subtle text-theme-badge-accent-text">
                  <Clock size={20} />
                </span>
                <span className="rounded-full bg-theme-accent-subtle px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-theme-badge-accent-text">
                  In progress
                </span>
                <Text
                  as="p"
                  className="max-w-sm text-[13px] leading-[1.7] text-theme-text-secondary"
                >
                  Our team is preparing your LinkedIn carousel and will share the PDF here soon.
                  You'll be able to preview it, download it and approve it then.
                </Text>
              </div>
            ) : fileError || !file ? (
              <div className="flex h-[200px] items-center justify-center rounded-xl border border-theme-border-subtle bg-theme-bg-subtle">
                <Text as="p" className="text-[13px] text-theme-text-muted">
                  Couldn't load your carousel. Close this and try again.
                </Text>
              </div>
            ) : (
              <PdfCarousel data={file} />
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-theme-border-subtle pt-4">
            {available && file && (
              <button
                type="button"
                onClick={() => downloadPdf(file)}
                className="mr-auto inline-flex items-center gap-1.5 rounded-lg border border-theme-border px-4 py-2 text-[12.5px] font-semibold text-theme-text-secondary transition-colors hover:bg-theme-bg-hover"
              >
                <Download size={14} /> Download PDF
              </button>
            )}
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg border border-theme-border px-4 py-2 text-[12.5px] font-semibold text-theme-text-secondary transition-colors hover:bg-theme-bg-hover"
              >
                Close
              </button>
            </Dialog.Close>
            {available && file && (
              <button
                type="button"
                onClick={onApprove}
                className="rounded-lg bg-primary-purple px-4 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
              >
                Approve carousel
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
